import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { SimpleFieldset } from "../../components/simplePage/simpleFieldset.tsx";
import { servicePost } from "../../api/client.ts";
import { FwRichTextarea } from "../../components/ui/fwRichTextarea.tsx";
import styles from "../../styles/formPage.module.scss";
import type { ProductEditorHandle, ProductEditorProps } from "./editProductShared.ts";

interface RoboticsProduct {
  ProductID: number;
  OperatorID: number;
  Name: string;
  Description: string;
  Flavours: string;
  Effects: string;
  MedicalUses: string;
  Amount: string;
  AmountType: string;
  Price: number;
  Tax: number;
  ImageUrl: string;
}

export const EditProductRobotics = forwardRef<ProductEditorHandle, ProductEditorProps>(
  function EditProductRobotics({ productId, onSaved, onStateChange }, ref) {
    const [product, setProduct] = useState<RoboticsProduct>({
      ProductID: 0, OperatorID: 0,
      Name: "", Description: "", Flavours: "", Effects: "",
      MedicalUses: "", Amount: "", AmountType: "",
      Price: 0, Tax: 0, ImageUrl: "",
    });
    const [price, setPrice] = useState("");
    const [tax, setTax] = useState("");
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [dirty, setDirty] = useState(false);

    const isNew = !productId;

    // Bubble saving + status + dirty to the outer Modal footer.
    useEffect(() => {
      onStateChange?.({ saving, status, dirty });
    }, [saving, status, dirty, onStateChange]);

    useEffect(() => {
      if (productId <= 0) {
        setProduct({
          ProductID: 0, OperatorID: 0,
          Name: "", Description: "", Flavours: "", Effects: "",
          MedicalUses: "", Amount: "", AmountType: "",
          Price: 0, Tax: 0, ImageUrl: "",
        });
        setPrice("");
        setTax("");
        setDirty(false);
        return;
      }
      setLoading(true);
      servicePost<RoboticsProduct>("GetProductRobotics", { id: productId })
        .then((result) => {
          if (!result.bError && result.oData) {
            setProduct(result.oData);
            setPrice(String(result.oData.Price ?? ""));
            setTax(String(result.oData.Tax ?? ""));
            setDirty(false);
          } else {
            setStatus({ type: "error", text: "Failed to load product" });
          }
        })
        .finally(() => setLoading(false));
    }, [productId]);

    const handleChange = useCallback((field: keyof RoboticsProduct, value: string) => {
      setProduct((prev) => ({ ...prev, [field]: value }));
      setDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
      if (!product.Name.trim()) {
        setStatus({ type: "error", text: "You need to give the product a name." });
        return;
      }
      setSaving(true);
      setStatus(null);
      try {
        const result = await servicePost<RoboticsProduct>("SaveRoboticsProduct", {
          product,
          price: parseFloat(price) || 0,
          tax: parseFloat(tax) || 0,
          forceUpdate: false,
        });
        if (result.bError || !result.oData) {
          setStatus({ type: "error", text: result.sMessage ?? "Failed to save product" });
        } else {
          setProduct(result.oData);
          setDirty(false);
          setStatus({ type: "success", text: result.oData.ProductID > 0 ? "Product Updated" : "Product Created" });
          onSaved();
        }
      } finally {
        setSaving(false);
      }
    }, [product, price, tax, onSaved]);

    useImperativeHandle(ref, () => ({ save: handleSave }), [handleSave]);

    if (loading) return <p>Loading...</p>;

    return (
      <>
        <SimpleFieldset legend="Product Information">
          <div className={styles.fieldRowStacked}>
            <label>Name</label>
            <input className={styles.fieldInput} value={product.Name} onChange={(e) => handleChange("Name", e.target.value)} />
          </div>
          <div className={styles.fieldRowStacked}>
            <label>Description</label>
            <FwRichTextarea
              value={product.Description}
              onChange={(v) => handleChange("Description", v)}
              rows={5}
            />
            <p className={styles.hint}>Wrap text in &lt;fw&gt;…&lt;/fw&gt; to use fixed-width font for that block (e.g. for aligned nutrition tables).</p>
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Flavours</label>
              <input className={styles.fieldInput} value={product.Flavours} onChange={(e) => handleChange("Flavours", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Effects</label>
              <input className={styles.fieldInput} value={product.Effects} onChange={(e) => handleChange("Effects", e.target.value)} />
            </div>
          </div>
          <div className={styles.fieldRowStacked}>
            <label>Medical Uses</label>
            <input className={styles.fieldInput} value={product.MedicalUses} onChange={(e) => handleChange("MedicalUses", e.target.value)} />
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Amount</label>
              <input className={styles.fieldInput} value={product.Amount} onChange={(e) => handleChange("Amount", e.target.value)} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Amount Type</label>
              <input className={styles.fieldInput} value={product.AmountType} onChange={(e) => handleChange("AmountType", e.target.value)} />
            </div>
          </div>
        </SimpleFieldset>

        <SimpleFieldset legend="Pricing">
          <div style={{ display: "flex", gap: "var(--space-3)" }}>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Buy Price</label>
              <input className={styles.fieldInput} type="number" step="0.01" min="0" value={price} onChange={(e) => { setPrice(e.target.value); setDirty(true); }} />
            </div>
            <div className={styles.fieldRowStacked} style={{ flex: 1 }}>
              <label>Buy Tax</label>
              <input className={styles.fieldInput} type="number" step="0.01" min="0" value={tax} onChange={(e) => { setTax(e.target.value); setDirty(true); }} />
            </div>
          </div>
        </SimpleFieldset>

        <SimpleFieldset legend="Product Image">
          <div className={styles.fieldRowStacked}>
            <label>Image File</label>
            <input className={styles.fieldInput} value={product.ImageUrl} onChange={(e) => handleChange("ImageUrl", e.target.value)} readOnly />
          </div>
          {product.ImageUrl && (
            <div style={{ textAlign: "center", padding: "var(--space-2)" }}>
              <img
                src={product.ImageUrl}
                alt={product.Name}
                style={{ maxHeight: 200, maxWidth: 300, border: "1px solid var(--color-border-default)" }}
              />
            </div>
          )}
          {isNew && (
            <p className={styles.hint} style={{ color: "var(--color-error)" }}>
              You need to create the product before you can upload an image.
            </p>
          )}
        </SimpleFieldset>
      </>
    );
  },
);
